/**
 * DPR frontend entry.
 * - home: reveal, intro, YouTube, hero, artist, shop, auth UI.
 * - admin: password toggle, admin forms.
 * - 라우트 연동: data-controller / data-action 에 따라 해당 TS 컨트롤러 메소드만 추가 실행.
 */
import { DomHelpers } from "@/utils/DomHelpers";
import { Logger } from "./utils/Logger";
import { Toast, showFlashToasts } from "./utils/Toast";
import { Modal } from "./utils/Modal";

// Home – global, controllers, components
import { initSiteDrawer } from "./home/global/SiteDrawer";
import { initHeaderScroll } from "./home/global/HeaderScroll";
import { initGoToTop } from "./home/global/GoToTop";
import { initReveal } from "./home/global/RevealController";
import { initSubGalleryFade } from "./home/global/SubGalleryFadeController";
import { initMainPopup } from "./home/global/MainPopupController";
import { IntroController } from "./home/controllers/IntroController";
import { HomeController } from "./home/controllers/HomeController";
import { AuthController } from "./home/controllers/AuthController";
import { HeroSlider } from "./home/components/HeroSlider";
import { HeroCursor } from "./home/components/HeroCursor";
import { YoutubeClipManager } from "./home/components/YoutubeClipManager";
import { ArtistVideoController } from "./home/components/ArtistVideoController";
import { initVideoModal } from "./home/components/VideoModalController";
import { ArtistCursor } from "./home/components/ArtistCursor";
import { ShopSlider } from "./home/components/ShopSlider";
import { ArtSlider } from "./home/components/ArtSlider";
import { PasswordToggleInput } from "./home/components/PasswordToggleInput";
import { CountrySelect } from "./home/components/CountrySelect";

// Admin – global, controllers (admin.php 라우트와 동기화)
import {
    AdminAuthController,
    AdminNoticeController,
    AdminController,
    BoardController,
    TagController,
    PostApprovalController,
    RoleController,
    InvitationController,
    UserController,
    AdminArtistController,
    AdminContactController,
    AdminMainVideoController,
    AdminMusicController,
    AdminShopProductController,
    AdminMusicVisualController,
    AdminPlaylistController,
    AdminSiteinfoController,
    AdminSiteServiceController,
    AdminServiceItemController,
    AdminInsightNewsController,
} from "./admin/controllers";
import {
    initAdminForms,
    initAdminDateTimeInputs,
    initAdminSelects,
    initFileUploaders,
    initAdminLayoutSidebar,
    initAdminNotificationsDropdown,
    initAdminUserDestroyButtons,
} from "./admin/global";

type ControllerAction = () => void;
type ControllerMap = Record<string, ControllerAction>;

/** PHP current_controller() / current_action() 와 이름 일치 (라우트 기반 자동 디스패치) */
const CONTROLLER_REGISTRY: Record<string, ControllerMap> = {
    AdminAuthController,
    AdminNoticeController,
    AdminController,
    BoardController,
    TagController,
    PostApprovalController,
    RoleController,
    InvitationController,
    UserController,
    AdminArtistController,
    AdminContactController,
    AdminMainVideoController,
    AdminMusicController,
    AdminShopProductController,
    AdminMusicVisualController,
    AdminPlaylistController,
    AdminSiteinfoController,
    AdminSiteServiceController,
    AdminServiceItemController,
    AdminInsightNewsController,
    AuthController,
    HomeController,
};

function getScriptAttrs(): { controller: string; action: string } {
    const script = document.querySelector<HTMLScriptElement>(
        'script[src*="app.js"]',
    );
    let controller = script ? (script.getAttribute("data-controller") ?? "").trim() : "";
    let action = script ? (script.getAttribute("data-action") ?? "").trim() : "";
    if (!controller || !action) {
        const page = document.querySelector(".admin-page");
        if (page) {
            controller = (page.getAttribute("data-controller") ?? "").trim() || controller;
            action = (page.getAttribute("data-action") ?? "").trim() || action;
        }
    }
    return { controller, action };
}

function dispatchController(controller: string, action: string): void {
    if (!controller || !action) return;
    const ctrl = CONTROLLER_REGISTRY[controller];
    if (!ctrl) {
        Logger.debug("app.dispatch: unknown controller", controller);
        return;
    }
    const fn = (ctrl as ControllerMap)[action];
    if (typeof fn !== "function") {
        Logger.debug("app.dispatch: unknown action", controller, action);
        return;
    }
    try {
        fn.call(ctrl);
    } catch (e) {
        Logger.error("app.dispatch failed", controller, action, e);
    }
}

/** 레이아웃에서 aos.js 로드 — 미호출 시 [data-aos] 요소가 opacity:0으로 남음 */
function initAos(): void {
    const w = window as unknown as {
        AOS?: { init: (opts?: Record<string, unknown>) => void; refresh: () => void };
    };
    const aos = w.AOS;
    if (!aos?.init) return;

    const reduceMotion =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
        aos.init({ disable: true });
        return;
    }

    aos.init({
        duration: 800,
        easing: "ease-out-cubic",
        once: true,
        offset: 80,
        delay: 0,
    });

    window.addEventListener(
        "load",
        () => {
            aos.refresh?.();
        },
        { once: true },
    );
}

function initPagination(): void {
    document.querySelectorAll(".no-pagination a[data-page]").forEach((link) => {
        link.addEventListener("click", (e) => {
            const page = (e.currentTarget as HTMLElement).getAttribute("data-page");
            if (!page || (e.currentTarget as HTMLAnchorElement).hasAttribute("disabled")) {
                e.preventDefault();
                return;
            }
            e.preventDefault();
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.set("page", page);
            window.location.href = window.location.pathname + "?" + urlParams.toString();
        });
    });
}

function runGlobalInits(): void {
    initPagination();

    const isAdmin = document.body.classList.contains("admin");

    if (isAdmin) {
        showFlashToasts();
        new PasswordToggleInput(document).init();
        initAdminForms();
        initAdminDateTimeInputs();
        initAdminSelects();
        initFileUploaders();
        initAdminLayoutSidebar();
        initAdminNotificationsDropdown();
        initAdminUserDestroyButtons();
        return;
    }

    initAos();

    if (document.querySelector(".main-popup-wrap")) initMainPopup();
    initSiteDrawer();
    initHeaderScroll();
    initGoToTop();
    initReveal();
    initSubGalleryFade();
    new IntroController().init();

    YoutubeClipManager.onReady(() => YoutubeClipManager.initAll());

    initVideoModal();

    const heroSlider = new HeroSlider();
    heroSlider.init();
    const heroEl = document.querySelector<HTMLElement>(".no-main-hero__slider");
    if (heroEl) {
        try {
            new HeroCursor(heroEl).init();
        } catch {
            /* no .cursor-title */
        }
    }

    const artistSection = DomHelpers.qs<HTMLElement>(document, ".no-artist");
    if (artistSection) {
        try {
            new ArtistVideoController(artistSection).init();
        } catch {
            /* missing elements */
        }
        try {
            new ArtistCursor(artistSection).init();
        } catch {
            /* no .cursor-title */
        }
    }

    new ShopSlider().init();

    if (document.querySelector(".sub-artist-view .art-slider")) {
        new ArtSlider().init();
    }

    new PasswordToggleInput(
        document.querySelector(".auth-page") ??
            document.querySelector(".profile-page") ??
            document,
    ).init();
    new CountrySelect("#auth-country").init();
}

function init(): void {
    (window as unknown as { Toast: typeof Toast; Modal: typeof Modal }).Toast =
        Toast;
    (window as unknown as { Toast: typeof Toast; Modal: typeof Modal }).Modal =
        Modal;
    runGlobalInits();

    const { controller, action } = getScriptAttrs();
    if (controller && action) {
        dispatchController(controller, action);
    }
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}
